<template>
  <div v-if="user" id="editUser" @change="dataChanged">
    <c-card>
      <c-card-body>
        <h1>Edit user: {{ [user.firstName, user.middleName, user.lastName].join(' ') }}</h1>
      </c-card-body>
    </c-card>
    <c-row>
      <c-col sm="12" md="3">
        <c-input
          v-model="firstName"
          type="text"
          label="First name"
          placeholder="Enter first name"
          :is-valid="checkIfValid('firstName')"
          invalid-feedback="Please enter a first name"
        />
        <c-input
          v-model="lastName"
          type="text"
          label="Last name"
          placeholder="Enter last name"
          :is-valid="checkIfValid('lastName')"
          invalid-feedback="Please enter a last name"
        />
        <c-input v-model="middleName" type="text" label="Middle name" placeholder="Enter middle name" />
      </c-col>
      <c-col sm="12" md="3">
        <template v-if="canChangeRoleOrLoginStatus">
          <c-select
            :options="roles"
            label="Role"
            :value="role"
            @update:value="
              e => {
                this.role = e;
              }
            "
          />
          <c-select
            v-if="user.accountConfirmed"
            :options="yesNoOptions"
            label="Allow user login"
            :value="allowLogin"
            @update:value="
              e => {
                this.allowLogin = e;
              }
            "
          />
          <label class="label">Group</label>
          <div v-for="group in groups">
            <label>
              <input v-model="groupIds" type="checkbox" :value="group._id" />
              {{ group.name }}
            </label>
          </div>
        </template>
      </c-col>
    </c-row>
    <c-button-group>
      <c-button color="primary" @click="save">
        <c-spinner v-if="isSaving" color="success" size="sm" />
        <span v-else>Update</span>
      </c-button>
      <c-button color="secondary" @click="checkCancel">Cancel</c-button>
    </c-button-group>
    <c-modal title="Discard your changes?" :show="showModal">
      <p class="lead">Any unsaved changes will be lost. Cannot undo.</p>
      <template v-slot:footer>
        <c-button-group>
          <c-button color="primary" @click="cancel">
            OK
          </c-button>
          <c-button color="secondary" @click="showModal = false">Cancel</c-button>
        </c-button-group>
      </template>
    </c-modal>
  </div>
</template>

<script>
import { validationMixin } from 'vuelidate';
import { required, email } from 'vuelidate/lib/validators';
import _ from 'lodash';
import { buildEndpoint } from '@/libs/buildEndPoint';
import { goToPage } from '@/libs/ui/goToPage';

export default {
  name: 'EditUser',
  mixins: [validationMixin],
  data() {
    return {
      isSaving: false,
      showModal: false,
      user: null,
      groups: [],
      yesNoOptions: [
        {
          value: 'y',
          label: 'Yes',
        },
        {
          value: 'n',
          label: 'No',
        },
      ],
      unsavedChanges: null,
      firstName: '',
      lastName: '',
      middleName: '',
      role: '',
      active: '',
      allowLogin: '',
      groupIds: [],
      canChangeRoleOrLoginStatus: false,
    };
  },
  validations: {
    firstName: {
      required,
    },
    lastName: {
      required,
    },
    role: {
      required,
    },
    allowLogin: {
      required,
    },
  },
  computed: {
    isValid() {
      return !this.$v.$invalid;
    },
    roles() {
      return this.$store.state.app.clientRoles.map((role) => ({
        value: role.key,
        label: role.name,
      }));
    },
  },
  mounted() {
    this.get();
    this.getAllGroups(() => {
      // Do nothing..
    });
  },
  methods: {
    checkIfValid(fieldName) {
      const field = this.$v[fieldName];
      if (!field.$dirty) {
        return null;
      }
      return !(field.$invalid || field.$model === '');
    },
    dataChanged() {
      this.unsavedChanges = true;
    },
    /**
     * Check for unsaved changes, show warning.
     */
    checkCancel() {
      if (this.unsavedChanges) {
        this.showModal = true;
        return;
      }
      this.cancel();
    },
    /**
     * Return to companies screen
     */
    cancel() {
      this.showModal = false;
      goToPage(this.$router, { name: 'detailsUser', params: { userId: this.$route.params.userId } });
    },
    /**
     * Close modal, return to page.
     */
    closeConfirmModal() {
      this.showModal = false;
    },
    get() {
      this.$http
        .get(buildEndpoint('organisation-admin/organisation/users/get/') + this.$route.params.userId)
        .then((response) => {
          if (response.data.success) {
            this.user = response.data.data.user;
            this.firstName = response.data.data.user.firstName;
            this.lastName = response.data.data.user.lastName;
            this.middleName = response.data.data.user.middleName;
            this.role = response.data.data.user.role;
            this.groupIds = response.data.data.user.groupIds;
            this.allowLogin = response.data.data.user.allowLogin ? 'y' : 'n';
            // only allow this if the user is not updating their own account - they could lock themselves out.
            this.canChangeRoleOrLoginStatus = this.$route.params.userId !== this.$store.state.app.authenticatedUser.id;
          } else {
            this.$store.commit('addErrorMessage', {
              header: 'Error',
              message: response.data.message,
            });
          }
        })
        .catch((error) => {
          this.$store.commit('addErrorMessage', {
            header: 'Error',
            message: error,
          });
        });
    },

    save() {
      this.$v.$touch();
      if (!this.isValid) {
        return;
      }

      this.isSaving = true;

      const data = {
        id: this.$route.params.userId,
        firstName: this.firstName,
        lastName: this.lastName,
        middleName: this.middleName,
        role: this.role,
        groupIds: this.groupIds,
        allowLogin: this.allowLogin === 'y',
      };

      this.$http
        .post(buildEndpoint('organisation-admin/organisation/users/update'), data)
        .then((response) => {
          this.isSaving = false;
          if (response.data.success) {
            this.$store.commit('addSuccessMessage', {
              header: 'Success',
              message: 'User details updated successfully',
            });
            this.$router.push({ name: 'detailsUser', params: { userId: this.$route.params.userId } });
            // all good
          } else {
            this.$store.commit('addErrorMessage', {
              header: 'Error',
              message: response.data.message,
            });
          }
        })
        .catch((error) => {
          this.isSaving = false;
          this.$store.commit('addErrorMessage', {
            header: 'Error',
            message: error.message,
          });
        });
    },
    getAllGroups(callback) {
      this.$http
        .get(buildEndpoint('organisation-admin/organisation/groups/get-all'), {})
        .then((response) => {
          if (response.data.success) {
            this.groups = _.sortBy(response.data.data.groups, (group) => group.name.toLowerCase());
          } else {
            this.$store.commit('addErrorMessage', {
              header: 'Error',
              message: response.data.message,
            });
          }
          return callback();
        })
        .catch((error) => {
          this.$store.commit('addErrorMessage', {
            header: 'Error',
            message: error.message,
          });
          return callback();
        });
    },
  },
};
</script>

<style lang="scss" scoped="">
.error-message {
  margin-bottom: 20px;
}
</style>
